import type { KodiakTlaPcbaMatchRow } from "./kodiakTlaPcbaMatchUi";
import type {
  KodiakPrefixAssignmentBoard,
  KodiakPrefixAssignmentHoverDetail,
  KodiakPrefixAssignmentHoverRow,
  KodiakDiscoveredPrefixHoverDetail,
} from "./kodiakPrefixAssignmentUi";

const KODIAK_MATCH_SESSION_CACHE_KEY = "mrp-tla-planning-kodiak-pcba-match:v4";
const KODIAK_MATCH_SESSION_CACHE_AT_KEY = "mrp-tla-planning-kodiak-pcba-match:at:v4";

export interface KodiakMatchSessionCachePayload {
  rows: KodiakTlaPcbaMatchRow[];
  prefix_board: KodiakPrefixAssignmentBoard;
  line_complete_locks: number[];
}

export function readKodiakMatchSessionCache(): KodiakMatchSessionCachePayload | undefined {
  try {
    const raw = sessionStorage.getItem(KODIAK_MATCH_SESSION_CACHE_KEY);
    if (raw == null || raw.trim() === "") return undefined;
    const parsed = JSON.parse(raw) as KodiakMatchSessionCachePayload;
    if (!Array.isArray(parsed.rows) || parsed.prefix_board == null) return undefined;
    const prefixBoard = normalizeKodiakPrefixAssignmentBoard(parsed.prefix_board);
    if (prefixBoard == null) return undefined;
    const lineCompleteLocks = Array.isArray(parsed.line_complete_locks)
      ? parsed.line_complete_locks.map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : [];
    return { rows: parsed.rows, prefix_board: prefixBoard, line_complete_locks: lineCompleteLocks };
  } catch {
    return undefined;
  }
}

function kodiakTlaSkuShortLabel(fullSku: string): string {
  const trimmed = fullSku.trim();
  let conversionPrefix = "";
  let remainder = trimmed;
  const babaMatch = remainder.match(/^(BABA-)/i);
  if (babaMatch?.[1] != null) {
    conversionPrefix = "BABA-";
    remainder = remainder.slice(babaMatch[1].length);
  } else {
    const abbMatch = remainder.match(/^(ABB-)/i);
    if (abbMatch?.[1] != null) {
      conversionPrefix = "ABB-";
      remainder = remainder.slice(abbMatch[1].length);
    }
  }
  const normalized = remainder.replace(/^ALT-/i, "");
  const fi5a = normalized.match(/FI-5A-([NW])-TLA/i);
  if (fi5a?.[1] != null) {
    return `${conversionPrefix}${fi5a[1].toUpperCase()}-TLA`;
  }
  const fi3c = normalized.match(/-([NBQ])-TLA-([AC])/i);
  if (fi3c?.[1] != null && fi3c[2] != null) {
    return `${conversionPrefix}${fi3c[1].toUpperCase()}-TLA-${fi3c[2].toUpperCase()}`;
  }
  return conversionPrefix === "" ? normalized : `${conversionPrefix}${normalized}`;
}

function normalizeHoverRow(value: unknown): KodiakPrefixAssignmentHoverRow | null {
  if (value == null || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  return {
    pcba_sku: String(row.pcba_sku ?? "—"),
    tla_sku: String(row.tla_sku ?? "—"),
    serial_fci_count: Number(row.serial_fci_count ?? 0),
    qc_incident_count: Number(row.qc_incident_count ?? 0),
    serial_begin_date:
      row.serial_begin_date == null || String(row.serial_begin_date).trim() === ""
        ? null
        : String(row.serial_begin_date).slice(0, 10),
    serial_end_date:
      row.serial_end_date == null || String(row.serial_end_date).trim() === ""
        ? null
        : String(row.serial_end_date).slice(0, 10),
  };
}

function normalizeHoverEntry(value: unknown): KodiakPrefixAssignmentHoverDetail | null {
  if (value == null || typeof value !== "object") return null;
  const hover = value as Record<string, unknown>;
  const purchaseOrderId = Number(hover.purchase_order_id);
  const pcbaBuildOrderId = Number(hover.pcba_build_order_id);
  const pcbaPrefix = String(hover.pcba_prefix ?? "").trim();
  if (!Number.isFinite(purchaseOrderId) || !Number.isFinite(pcbaBuildOrderId) || pcbaPrefix === "") {
    return null;
  }

  const directRows = Array.isArray(hover.rows)
    ? hover.rows.map(normalizeHoverRow).filter((row): row is KodiakPrefixAssignmentHoverRow => row != null)
    : [];

  if (directRows.length > 0) {
    return {
      purchase_order_id: purchaseOrderId,
      pcba_build_order_id: pcbaBuildOrderId,
      pcba_prefix: pcbaPrefix,
      rows: directRows,
      rewire_note:
        hover.rewire_note == null || String(hover.rewire_note).trim() === ""
          ? null
          : String(hover.rewire_note),
    };
  }

  const legacyTlaLines = hover.tla_lines;
  if (Array.isArray(legacyTlaLines) && legacyTlaLines.length > 0) {
    const pcbaSku = String(hover.pcba_sku ?? "—");
    const serialBeginDate =
      hover.serial_begin_date == null ? null : String(hover.serial_begin_date).slice(0, 10);
    const serialEndDate = hover.serial_end_date == null ? null : String(hover.serial_end_date).slice(0, 10);
    return {
      purchase_order_id: purchaseOrderId,
      pcba_build_order_id: pcbaBuildOrderId,
      pcba_prefix: pcbaPrefix,
      rows: legacyTlaLines
        .map((line) => {
          if (line == null || typeof line !== "object") return null;
          const record = line as Record<string, unknown>;
          const tlaSku = String(record.tla_sku ?? "").trim();
          if (tlaSku === "") return null;
          return {
            pcba_sku: pcbaSku,
            tla_sku: kodiakTlaSkuShortLabel(tlaSku),
            serial_fci_count: Number(record.unique_serial_count ?? 0),
            qc_incident_count: 0,
            serial_begin_date: serialBeginDate,
            serial_end_date: serialEndDate,
          };
        })
        .filter((row): row is KodiakPrefixAssignmentHoverRow => row != null),
    };
  }

  return {
    purchase_order_id: purchaseOrderId,
    pcba_build_order_id: pcbaBuildOrderId,
    pcba_prefix: pcbaPrefix,
    rows: [],
  };
}

function normalizeDiscoveredHoverEntry(value: unknown): KodiakDiscoveredPrefixHoverDetail | null {
  if (value == null || typeof value !== "object") return null;
  const hover = value as Record<string, unknown>;
  const pcbaPrefix = String(hover.pcba_prefix ?? "").trim();
  const coreFeature = String(hover.core_feature ?? "").trim();
  if (pcbaPrefix === "" || coreFeature === "") return null;
  const rows = Array.isArray(hover.rows)
    ? hover.rows.map(normalizeHoverRow).filter((row): row is KodiakPrefixAssignmentHoverRow => row != null)
    : [];
  return { pcba_prefix: pcbaPrefix, core_feature: coreFeature, rows };
}

function normalizeKodiakPrefixAssignmentBoard(
  board: KodiakPrefixAssignmentBoard,
): KodiakPrefixAssignmentBoard | undefined {
  if (!Array.isArray(board.discovered_prefixes) || !Array.isArray(board.assignments)) {
    return undefined;
  }
  return {
    discovered_prefixes: board.discovered_prefixes.map((entry) => ({
      prefix: String(entry.prefix ?? ""),
      serial_fci_count: Number(entry.serial_fci_count ?? 0),
      qc_incident_count: Number(entry.qc_incident_count ?? 0),
      serial_begin_date:
        entry.serial_begin_date == null ? null : String(entry.serial_begin_date).slice(0, 10),
      serial_end_date: entry.serial_end_date == null ? null : String(entry.serial_end_date).slice(0, 10),
      core_feature:
        entry.core_feature == null || String(entry.core_feature).trim() === ""
          ? null
          : String(entry.core_feature),
    })),
    pcba_targets: Array.isArray(board.pcba_targets) ? board.pcba_targets : [],
    discovered_prefix_hover: Array.isArray(board.discovered_prefix_hover)
      ? board.discovered_prefix_hover
          .map(normalizeDiscoveredHoverEntry)
          .filter((hover): hover is KodiakDiscoveredPrefixHoverDetail => hover != null)
      : [],
    planned_prefixes: Array.isArray(board.planned_prefixes)
      ? board.planned_prefixes.map((planned) => ({
          pcba_prefix: String(planned.pcba_prefix ?? ""),
          core_feature: String(planned.core_feature ?? ""),
          expected_tla_sku: String(planned.expected_tla_sku ?? ""),
          purchase_order_id:
            planned.purchase_order_id == null ? null : Number(planned.purchase_order_id),
          pcba_build_order_id:
            planned.pcba_build_order_id == null ? null : Number(planned.pcba_build_order_id),
          tla_build_order_id:
            planned.tla_build_order_id == null ? null : Number(planned.tla_build_order_id),
          tla_work_order_number:
            planned.tla_work_order_number == null || String(planned.tla_work_order_number).trim() === ""
              ? null
              : String(planned.tla_work_order_number),
          po_number:
            planned.po_number == null || String(planned.po_number).trim() === ""
              ? null
              : String(planned.po_number),
          notes: planned.notes == null || String(planned.notes).trim() === "" ? null : String(planned.notes),
          allowed_tla_skus: Array.isArray(planned.allowed_tla_skus)
            ? planned.allowed_tla_skus.map((sku) => String(sku))
            : null,
        }))
      : [],
    assignments: board.assignments.map((assignment) => ({
      ...assignment,
      purchase_order_id: Number(assignment.purchase_order_id),
      pcba_build_order_id: Number(assignment.pcba_build_order_id),
      core_feature: String(assignment.core_feature ?? ""),
    })),
    assignment_hover: Array.isArray(board.assignment_hover)
      ? board.assignment_hover
          .map(normalizeHoverEntry)
          .filter((hover): hover is KodiakPrefixAssignmentHoverDetail => hover != null)
      : [],
  };
}

export function readKodiakMatchSessionCacheUpdatedAt(): number {
  try {
    const raw = sessionStorage.getItem(KODIAK_MATCH_SESSION_CACHE_AT_KEY);
    if (raw == null || raw.trim() === "") return 0;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

export function writeKodiakMatchSessionCache(payload: KodiakMatchSessionCachePayload): void {
  try {
    const normalizedBoard = normalizeKodiakPrefixAssignmentBoard(payload.prefix_board);
    if (normalizedBoard == null || !Array.isArray(payload.rows)) {
      return;
    }
    sessionStorage.removeItem("mrp-tla-planning-kodiak-pcba-match");
    sessionStorage.removeItem("mrp-tla-planning-kodiak-pcba-match:at");
    sessionStorage.removeItem("mrp-tla-planning-kodiak-pcba-match:v2");
    sessionStorage.removeItem("mrp-tla-planning-kodiak-pcba-match:at:v2");
    sessionStorage.removeItem("mrp-tla-planning-kodiak-pcba-match:v3");
    sessionStorage.removeItem("mrp-tla-planning-kodiak-pcba-match:at:v3");
    sessionStorage.setItem(
      KODIAK_MATCH_SESSION_CACHE_KEY,
      JSON.stringify({
        rows: payload.rows,
        prefix_board: normalizedBoard,
        line_complete_locks: Array.isArray(payload.line_complete_locks)
          ? payload.line_complete_locks.map((id) => Number(id))
          : [],
      }),
    );
    sessionStorage.setItem(KODIAK_MATCH_SESSION_CACHE_AT_KEY, String(Date.now()));
  } catch {
    /* private mode or quota */
  }
}
